import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { ApplicationFacade, OpenTripPlannerFacade } from '@ridango/components';
import { BaseComponent } from '@ridango/core';
import { combineLatest, Observable, EMPTY } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import Env from '@ridango/core/env';
import { Capacitor } from '@capacitor/core';
import {
    ExtendedStop,
    JourneyPlannerViaPointModalComponent
} from '../../journey-planner-via-point-modal/journey-planner-via-point-modal.component';

@Component({
    selector: 'rwidget-journey-planner-filter-via-point',
    templateUrl: './journey-planner-filter-via-point.component.html',
    styleUrls: ['./journey-planner-filter-via-point.component.scss']
})
export class JourneyPlannerFilterViaPointComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input() formGroup: FormGroup;
    @Input() formItem: string;

    readonly assetsPath$ = this.applicationFacade.assetsPath$;
    readonly stops$ = this.openTripPlannerFacade.stops$;

    selectedStop$: Observable<ExtendedStop | null>;
    selectedStopName$: Observable<string | null>;
    isContrast$ = this.applicationFacade.highContrast$;

    private viaPointSearchTerm = '';

    constructor(
        private applicationFacade: ApplicationFacade,
        private openTripPlannerFacade: OpenTripPlannerFacade,
        private modalCtrl: ModalController,
        @Inject('ENV') public env: Env
    ) {
        super();
    }

    ngOnInit() {
        if (!this.formGroup || !this.formItem) {
            console.warn('Missing formGroup or formItem inputs from JourneyPlannerFilterViaPointComponent');
            this.selectedStop$ = EMPTY;
            this.selectedStopName$ = EMPTY;
            return;
        }

        this.openTripPlannerFacade.getAllStops();
        this.ensureFormControlExists();
        this.setupObservables();
    }

    private ensureFormControlExists(): void {
        if (!this.formGroup.get(this.formItem)) {
            this.formGroup.addControl(this.formItem, new FormControl(null));
        }
    }

    private setupObservables(): void {
        const formControl = this.formGroup.get(this.formItem);

        if (!formControl) {
            this.selectedStop$ = EMPTY;
            this.selectedStopName$ = EMPTY;
            return;
        }

        const formValue$ = formControl.valueChanges.pipe(
            startWith(formControl.value),
            takeUntil(this.componentDestroyed$)
        );

        this.selectedStop$ = combineLatest([this.stops$, formValue$]).pipe(
            map(([stops, selectedStopId]) => {
                if (!stops || !selectedStopId) return null;
                return stops.find((stop) => stop.id === selectedStopId) || null;
            }),
            takeUntil(this.componentDestroyed$)
        );

        this.selectedStopName$ = this.selectedStop$.pipe(map((selectedStop) => selectedStop?.name || null));
    }

    async toggleViaPointModal() {
        const triggerElement = document.activeElement as HTMLElement;
        const currentValue = this.formGroup?.get(this.formItem)?.value;

        this.stops$
            .pipe(
                map(
                    (stops) =>
                        stops?.map((stop) => ({
                            ...stop,
                            selected: stop.id === currentValue
                        })) || []
                ),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe(async (currentStops) => {
                const selectedStop = currentStops.find((stop) => stop.id === currentValue);
                const initialSearchTerm = this.viaPointSearchTerm || selectedStop?.name || '';

                const modal = await this.modalCtrl.create({
                    component: JourneyPlannerViaPointModalComponent,
                    cssClass: 'journey-planner-via-point-modal',
                    componentProps: {
                        stops: currentStops,
                        selectedStopId: currentValue,
                        initialSearchTerm
                    }
                });

                this.handleModalFocus(modal);

                modal.onDidDismiss().then((result) => {
                    if (!result) {
                        triggerElement?.focus();
                        return;
                    }
                    const modalSearchTerm = typeof result.data?.searchTerm === 'string' ? result.data.searchTerm : null;
                    if (modalSearchTerm !== null) {
                        this.viaPointSearchTerm = modalSearchTerm;
                    }

                    if (result.role === 'selected') {
                        const selected = (result.data?.stop as ExtendedStop) || result.data;
                        const newValue = selected?.id || null;
                        const control = this.formGroup?.get(this.formItem);
                        if (control) {
                            control.setValue(newValue);
                        }
                    }

                    triggerElement?.focus();
                });

                await modal.present();
            });
    }

    private handleModalFocus(modal: HTMLIonModalElement) {
        if (Capacitor.getPlatform() !== 'android') return;

        modal.addEventListener(
            'didPresent',
            (ev: Event) => {
                const el = ev.currentTarget as HTMLElement;
                const header = el.querySelector<HTMLElement>('#via-point-modal-header');
                if (header) {
                    header.tabIndex = -1;
                    header.focus();
                }
            },
            { once: true }
        );
    }

    public resetSelection(): void {
        this.formGroup?.get(this.formItem)?.setValue(null);
        this.viaPointSearchTerm = '';
    }
}
